import { useQuery } from '@apollo/client/react';
import { Loader2Icon, XIcon } from 'lucide-react';
import { type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import Header from '../../Header';
import { RightSidebarWrapper } from '../../RightSidebar/RightSidebarWrapper';
import {
  formatScore,
  formatScoreOutOf5,
  GET_RISK_SCORECARD_HISTORY_DETAIL,
  type GetRiskScorecardHistoryDetailResponse,
  getScoreColor
} from '../graphql';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

const ScoreHeader = ({
  score,
  title,
  tone
}: {
  score: string;
  title: string;
  tone?: 'danger' | 'warning';
}) => (
  <div className="flex items-center justify-between">
    <div className="text-lg font-semibold">{title}</div>
    <div
      className={
        tone === 'danger'
          ? 'text-destructive text-lg font-semibold'
          : 'text-lg font-semibold text-yellow-500'
      }
    >
      {score}
    </div>
  </div>
);

const Card = ({ children }: { children: ReactNode }) => (
  <div className="bg-card rounded-md border p-5">{children}</div>
);

const ScoreRow = ({ label, score }: { label: ReactNode; score: string }) => (
  <div className="grid grid-cols-[1fr_auto] items-center gap-6">
    <div className="text-foreground text-sm leading-5 font-normal">{label}</div>
    <div className="text-muted-foreground text-right text-sm font-medium">
      {score}
    </div>
  </div>
);

const InvestmentsDirectIdDealScoreDetails = () => {
  const { dealScoreId, id } = useParams<{ dealScoreId: string; id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const scoreName = (location.state as { name?: string } | null)?.name ?? 'Deal Score Details';

  const { data, error, loading } =
    useQuery<GetRiskScorecardHistoryDetailResponse>(
      GET_RISK_SCORECARD_HISTORY_DETAIL,
      {
        skip: !dealScoreId,
        variables: { id: dealScoreId }
      }
    );

  const history = data?.node;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2Icon className="size-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive p-5">
        Error loading score details: {error.message}
      </div>
    );
  }

  if (!history) {
    return (
      <div className="text-muted-foreground p-5">Score record not found.</div>
    );
  }

  const getSectionTone = (
    score: null | number | undefined
  ): 'danger' | 'warning' => {
    if (score == null) return 'warning';
    return score < 33 ? 'danger' : 'warning';
  };

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="space-y-7 px-5 pt-5 pb-16.5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">{scoreName}</h3>
            <Button
              className="text-muted-foreground"
              onClick={() => { void navigate(`/investments/direct/${id}/deal-score`); }}
              variant="outline"
            >
              <XIcon className="size-4" />
            </Button>
          </div>
          <div className="space-y-7 pl-2">
          <div className="space-y-7">
            <ScoreHeader
              score={formatScore(history.totalScore)}
              title="Overall Score"
            />
            <Separator />
          </div>
          <section className="space-y-2.5">
            <ScoreHeader
              score={formatScore(history.dealStructureSectionScore)}
              title="Deal Structure"
              tone={getSectionTone(history.dealStructureSectionScore)}
            />
            <Card>
              <div className="space-y-5">
                <ScoreRow
                  label="i) Leverage Attachment Point"
                  score={formatScoreOutOf5(
                    history.leverageAttachmentPointScore
                  )}
                />
                <ScoreRow
                  label="ii) Leverage Detachment Point"
                  score={formatScoreOutOf5(
                    history.leverageDetachmentPointScore
                  )}
                />
                <ScoreRow
                  label="iii) Equity as % of Total Capital"
                  score={formatScoreOutOf5(
                    history.equityAsPercentOfTotalCapitalScore
                  )}
                />
                <ScoreRow
                  label="iv) Use of Proceeds"
                  score={formatScoreOutOf5(history.useOfProceedsScore)}
                />
                {history.dealStructureSynthesis && (
                  <div className="text-muted-foreground border-t pt-4 text-sm">
                    <strong>Synthesis:</strong> {history.dealStructureSynthesis}
                  </div>
                )}
              </div>
            </Card>
          </section>
          <section className="space-y-2.5">
            <ScoreHeader
              score={formatScore(history.businessModelSectionScore)}
              title="Business Model"
              tone={getSectionTone(history.businessModelSectionScore)}
            />
            <Card>
              <div className="space-y-5">
                <ScoreRow
                  label="i) Revenue Model (Re-Occurring / Recurring)"
                  score={formatScoreOutOf5(history.revenueModelScore)}
                />
                <ScoreRow
                  label="ii) Top 10 Customer Concentration"
                  score={formatScoreOutOf5(
                    history.top10CustomerConcentrationScore
                  )}
                />
                <ScoreRow
                  label="iii) 3-Year Organic Revenue CAGR"
                  score={formatScoreOutOf5(history.organicRevenueCagrScore)}
                />
                <ScoreRow
                  label="iv) 1-Year Organic Revenue Growth"
                  score={formatScoreOutOf5(history.organicRevenueGrowthScore)}
                />
                <ScoreRow
                  label="v.a) Service Gross Margin"
                  score={formatScoreOutOf5(
                    history.serviceGrossMarginThresholdsScore
                  )}
                />
                <ScoreRow
                  label="v.b) Product Gross Margin"
                  score={formatScoreOutOf5(
                    history.productGrossMarginThresholdsScore
                  )}
                />
                {history.businessModelSynthesis && (
                  <div className="text-muted-foreground border-t pt-4 text-sm">
                    <strong>Synthesis:</strong> {history.businessModelSynthesis}
                  </div>
                )}
              </div>
            </Card>
          </section>
          <section className="space-y-2.5">
            <ScoreHeader
              score={formatScore(history.operatingCashFlowSectionScore)}
              title="Operating / Cash Flow"
              tone={getSectionTone(history.operatingCashFlowSectionScore)}
            />
            <Card>
              <div className="space-y-5">
                <ScoreRow
                  label="i) EBITDA Margins"
                  score={formatScoreOutOf5(history.ebitdaMarginsScore)}
                />
                <ScoreRow
                  label="ii) (EBITDA – Capex) / Interest"
                  score={formatScoreOutOf5(history.ebitdaCapexInterest1Score)}
                />
                <ScoreRow
                  label="iii) Interest Coverage Ratio"
                  score={formatScoreOutOf5(history.interestCoverageRatioScore)}
                />
                <ScoreRow
                  label="iv) FCF Conversion %"
                  score={formatScoreOutOf5(
                    history.fcfConversionPercentageScore
                  )}
                />
                {history.operatingCashFlowSynthesis && (
                  <div className="text-muted-foreground border-t pt-4 text-sm">
                    <strong>Synthesis:</strong>{' '}
                    {history.operatingCashFlowSynthesis}
                  </div>
                )}
              </div>
            </Card>
          </section>
          <section className="space-y-2.5">
            <ScoreHeader
              score={formatScore(history.marketPositionSectionScore)}
              title="Market Position"
              tone={getSectionTone(history.marketPositionSectionScore)}
            />
            <Card>
              <ScoreRow
                label={
                  <span className="inline-flex flex-col gap-1">
                    <span>Market Attributes Exhibited of:</span>
                    <span>
                      Business Criticality, Barriers to Entry, Competitive
                      Advantages, Market Leader
                    </span>
                  </span>
                }
                score={formatScoreOutOf5(history.marketPositionScore)}
              />
            </Card>
          </section>
          <section className="space-y-2.5">
            <ScoreHeader
              score={formatScore(history.industryResilienceSectionScore)}
              title="Industry Resilience"
              tone={getSectionTone(history.industryResilienceSectionScore)}
            />
            <Card>
              <div className="space-y-5">
                <ScoreRow
                  label="i) Recession Resiliency, Industry Tailwinds/Headwinds"
                  score={formatScoreOutOf5(history.industryResilienceScore)}
                />
                {history.industryResilienceSynthesis && (
                  <div className="text-muted-foreground border-t pt-4 text-sm">
                    <strong>Synthesis:</strong>{' '}
                    {history.industryResilienceSynthesis}
                  </div>
                )}
              </div>
            </Card>
          </section>
          <section className="space-y-2.5">
            <ScoreHeader
              score={formatScore(history.sponsorManagementSectionScore)}
              title="Sponsor / Management"
              tone={getSectionTone(history.sponsorManagementSectionScore)}
            />
            <Card>
              <div className="space-y-5">
                <ScoreRow
                  label="i) Experienced Sponsor with Track Record [2], Management Ownership Rollover (min. 15% rollover of proceeds) [1], Management Retention [1], Management Experience [1]"
                  score={formatScoreOutOf5(history.sponsorManagementScore)}
                />
                {history.sponsorManagementSynthesis && (
                  <div className="text-muted-foreground border-t pt-4 text-sm">
                    <strong>Synthesis:</strong>{' '}
                    {history.sponsorManagementSynthesis}
                  </div>
                )}
              </div>
            </Card>
          </section>
          </div>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdDealScoreDetails;
